import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import {
  Box,
  Flex,
  Heading,
  Text,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  HStack,
  VStack,
  Button,
  Badge,
  Tooltip,
  useToast,
  Spinner,
  useDisclosure,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerCloseButton,
  Textarea,
  Stat,
  StatLabel,
  StatNumber,
  SimpleGrid,
  IconButton,
  Divider,
} from "@chakra-ui/react";
import {
  TimeIcon,
  InfoOutlineIcon,
  CopyIcon,
  CheckIcon,
} from "@chakra-ui/icons";
import { useCognito } from "../utils/useCognito";

// ==========================
// Config
// ==========================

// If you already centralize API base, swap this for your util.
const API_BASE = "https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod";
const POLL_MS = 30_000;

// Keep in sync with NotificationBell.jsx
const COUNTS_EVENT = "cg:notifications:updateCounts";
const COUNTS_END_EVENT = "cg:notifications:updateCounts:off";

// ==========================
// Small utils
// ==========================
const fmt = (iso) => {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso ?? "";
  }
};

const hoverScale1 = { transform: "scale(1)" };

// Facility address formatter: handles raw strings or JSON stringified objects
const formatFacilityAddress = (raw) => {
  if (!raw) return "";

  // If it looks like JSON, try to parse
  if (typeof raw === "string" && raw.trim().startsWith("{")) {
    try {
      const obj = JSON.parse(raw);
      return formatFacilityAddress(obj) || raw;
    } catch {
      return raw;
    }
  }

  // If it's already an object
  if (typeof raw === "object") {
    const { street, city, state, zip, npi, tin, ptan } = raw;
    const lines = [];

    const line1Parts = [];
    if (street) line1Parts.push(street);
    const cityStateZip = [city, state, zip].filter(Boolean).join(", ");
    if (cityStateZip) line1Parts.push(cityStateZip);
    if (line1Parts.length) lines.push(line1Parts.join(" • "));

    const meta = [];
    if (npi) meta.push(`NPI: ${npi}`);
    if (tin) meta.push(`TIN: ${tin}`);
    if (ptan) meta.push(`PTAN: ${ptan}`);
    if (meta.length) lines.push(meta.join(" • "));

    return lines.join("  ·  ");
  }

  // Fallback
  return String(raw);
};

const formatFacilityPhone = (raw) => {
  if (!raw) return "";
  const s = String(raw);
  // Very light formatting; you can swap for your real phone formatter
  if (s.length === 10 && /^\d+$/.test(s)) {
    return `(${s.slice(0, 3)}) ${s.slice(3, 6)}-${s.slice(6)}`;
  }
  return s;
};

// ==========================
// API client (minimal)
// ==========================
async function apiGet(path, token) {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: {
      Authorization: token ? `Bearer ${token}` : "",
    },
  });
  if (!res.ok) throw new Error(`GET ${path} -> ${res.status}`);
  return res.json();
}

async function apiPost(path, body, token) {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: token ? `Bearer ${token}` : "",
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(`POST ${path} -> ${res.status} ${txt}`);
  }
  return res.json();
}

// ==========================
// Row card
// ==========================
const NotificationCard = ({
  n,
  onOpenTimeline,
  onResolveLatest,
  isResolving,
}) => {
  const [copied, setCopied] = useState(false);

  const colorByBucket =
    n.bucket === "action_required"
      ? "red"
      : n.bucket === "confirm"
      ? "green"
      : "blue";

  const facilityAddress = formatFacilityAddress(n.facilityAddress);
  const facilityPhone = formatFacilityPhone(n.facilityPhone);
  const hasFacilityBlock =
    n.facilityName ||
    facilityAddress ||
    facilityPhone ||
    n.facilityEmail ||
    n.facilityNPI ||
    n.userEmail;

  const handleCopyUserId = async () => {
    if (!n.userId || !navigator?.clipboard) return;
    try {
      await navigator.clipboard.writeText(n.userId);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore; not critical
    }
  };

  return (
    <Box
      borderWidth="1px"
      rounded="xl"
      p={4}
      bg="white"
      borderColor="gray.200"
      _hover={{ borderColor: "teal.400", bg: "gray.50" }}
      transition="all 0.2s ease"
    >
      <HStack justify="space-between" align="start" mb={2}>
        <HStack spacing={3} align="center">
          <Badge colorScheme={colorByBucket}>{n.bucket}</Badge>
          {n.entityType && (
            <Badge colorScheme="purple" textTransform="uppercase">
              {n.entityType}
            </Badge>
          )}
          {n.entityId && <Badge colorScheme="cyan">{n.entityId}</Badge>}
        </HStack>
        <Text fontSize="xs" color="gray.500">
          {fmt(n.createdAt)}
        </Text>
      </HStack>

      <Text fontWeight="semibold" mb={1} color="gray.800">
        {n.event?.replaceAll("_", " ")}
      </Text>

      {/* Facility + user context */}
      {(hasFacilityBlock || n.userId) && (
        <Box
          mt={3}
          p={3}
          borderWidth="1px"
          borderColor="gray.200"
          rounded="md"
          bg="gray.50"
        >
          <HStack justify="space-between" align="flex-start" mb={2}>
            <Box>
              <Text fontSize="xs" color="gray.500" textTransform="uppercase">
                Facility
              </Text>
              <Text fontWeight="medium" color="gray.800">
                {n.facilityName || "Unknown facility"}
              </Text>
              {facilityAddress && (
                <Text fontSize="sm" color="gray.700">
                  {facilityAddress}
                </Text>
              )}
              <HStack spacing={4} mt={1}>
                {facilityPhone && (
                  <Text fontSize="xs" color="gray.600">
                    Phone: {facilityPhone}
                  </Text>
                )}
                {n.facilityEmail && (
                  <Text fontSize="xs" color="gray.600">
                    Email: {n.facilityEmail}
                  </Text>
                )}
              </HStack>
              {n.facilityNPI && (
                <Text fontSize="xs" color="gray.600" mt={1}>
                  NPI: {n.facilityNPI}
                </Text>
              )}
              {n.userEmail && (
                <Text fontSize="xs" color="gray.600" mt={1}>
                  User email: {n.userEmail}
                </Text>
              )}
            </Box>

            {/* userId copy block (no value shown) */}
            {n.userId && (
              <Box textAlign="right" minW="0">
                <Text
                  fontSize="xs"
                  fontFamily="mono"
                  color="gray.500"
                  mb={1}
                >
                  userId
                </Text>
                <HStack spacing={1} justify="flex-end">
                  <Tooltip label={copied ? "Copied!" : "Copy userId"}>
                    <IconButton
                      aria-label="Copy userId"
                      size="xs"
                      icon={copied ? <CheckIcon /> : <CopyIcon />}
                      onClick={handleCopyUserId}
                      variant="ghost"
                    />
                  </Tooltip>
                </HStack>
              </Box>
            )}
          </HStack>
        </Box>
      )}

      <HStack spacing={3} mt={3}>
        <Button
          size="sm"
          onClick={() => onOpenTimeline(n.groupId)}
          leftIcon={<InfoOutlineIcon />}
          _hover={hoverScale1}
        >
          View timeline
        </Button>

        <Tooltip label="Mark this notification group as read">
          <Button
            size="sm"
            colorScheme="teal"
            onClick={() => onResolveLatest(n.groupId)}
            isLoading={isResolving === n.groupId}
            _hover={hoverScale1}
          >
            Mark Read
          </Button>
        </Tooltip>
      </HStack>
    </Box>
  );
};

// ==========================
// Timeline Drawer
// ==========================
const TimelineDrawer = ({
  isOpen,
  onClose,
  groupId,
  items,
  onAddNote,
  onResolveLatest,
  loading,
  isResolving,
}) => {
  const [noteText, setNoteText] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setNoteText("");
      setCopied(false);
    }
  }, [isOpen]);

  const latest = items?.[0]; // newest first from API

  const facilityAddress = latest
    ? formatFacilityAddress(latest.facilityAddress)
    : "";
  const facilityPhone = latest ? formatFacilityPhone(latest.facilityPhone) : "";

  const handleCopyUserId = async () => {
    const userId = latest?.userId;
    if (!userId || !navigator?.clipboard) return;
    try {
      await navigator.clipboard.writeText(userId);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  };

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="lg">
      <DrawerOverlay />
      <DrawerContent bg="white">
        <DrawerCloseButton />
        <DrawerHeader>
          <VStack align="stretch" spacing={3}>
            <Box>
              <Text fontSize="sm" color="gray.400">
                Group
              </Text>
              <Text fontWeight="semibold" wordBreak="break-all">
                {groupId}
              </Text>
            </Box>

            {/* Facility + user context for the group (from latest item) */}
            {latest && (
              <Box
                borderWidth="1px"
                borderColor="gray.200"
                rounded="md"
                p={3}
                bg="gray.50"
              >
                <HStack align="flex-start" justify="space-between">
                  <Box>
                    <Text
                      fontSize="xs"
                      color="gray.500"
                      textTransform="uppercase"
                    >
                      Facility
                    </Text>
                    <Text fontWeight="medium" color="gray.800">
                      {latest.facilityName || "Unknown facility"}
                    </Text>
                    {facilityAddress && (
                      <Text fontSize="sm" color="gray.700">
                        {facilityAddress}
                      </Text>
                    )}
                    <HStack spacing={4} mt={1}>
                      {facilityPhone && (
                        <Text fontSize="xs" color="gray.600">
                          Phone: {facilityPhone}
                        </Text>
                      )}
                      {latest.facilityEmail && (
                        <Text fontSize="xs" color="gray.600">
                          Email: {latest.facilityEmail}
                        </Text>
                      )}
                    </HStack>
                    {latest.facilityNPI && (
                      <Text fontSize="xs" color="gray.600" mt={1}>
                        NPI: {latest.facilityNPI}
                      </Text>
                    )}
                    {latest.userEmail && (
                      <Text fontSize="xs" color="gray.600" mt={1}>
                        User email: {latest.userEmail}
                      </Text>
                    )}
                  </Box>

                  {latest.userId && (
                    <Box textAlign="right" minW="0">
                      <Text
                        fontSize="xs"
                        fontFamily="mono"
                        color="gray.500"
                        mb={1}
                      >
                        userId
                      </Text>
                      <HStack spacing={1} justify="flex-end">
                        <Tooltip label={copied ? "Copied!" : "Copy userId"}>
                          <IconButton
                            aria-label="Copy userId"
                            size="xs"
                            icon={copied ? <CheckIcon /> : <CopyIcon />}
                            onClick={handleCopyUserId}
                            variant="ghost"
                          />
                        </Tooltip>
                      </HStack>
                    </Box>
                  )}
                </HStack>
              </Box>
            )}

            <Button
              colorScheme="teal"
              onClick={() => onResolveLatest(groupId)}
              isLoading={isResolving === groupId}
              _hover={hoverScale1}
              width="full"
            >
              Mark Read
            </Button>
          </VStack>
        </DrawerHeader>
        <DrawerBody>
          {loading ? (
            <Flex align="center" justify="center" minH="200px">
              <Spinner />
            </Flex>
          ) : !items?.length ? (
            <Text color="gray.600">No timeline entries for this group.</Text>
          ) : (
            <VStack align="stretch" spacing={4}>
              {items.map((it) => (
                <Box
                  key={`${it.groupId}-${it.createdAt}`}
                  borderWidth="1px"
                  rounded="md"
                  p={3}
                  bg={it.status === "open" ? "blue.50" : "gray.100"}
                  borderColor="gray.200"
                >
                  <HStack justify="space-between" mb={1}>
                    <HStack>
                      <Badge
                        colorScheme={
                          it.bucket === "action_required"
                            ? "red"
                            : it.bucket === "confirm"
                            ? "green"
                            : "blue"
                        }
                      >
                        {it.bucket}
                      </Badge>
                      <Text fontWeight="semibold">
                        {it.event?.replaceAll("_", " ")}
                      </Text>
                    </HStack>
                    <Text fontSize="xs" color="gray.500">
                      {fmt(it.createdAt)}
                    </Text>
                  </HStack>
                  <HStack spacing={4} color="gray.600" fontSize="sm">
                    <Text>Status: {it.status}</Text>
                    {it.resolvedAt && (
                      <Text>Resolved: {fmt(it.resolvedAt)}</Text>
                    )}
                    {it.resolvedBy && <Text>By: {it.resolvedBy}</Text>}
                    {it.lastNoteAt && <Text>Note: {fmt(it.lastNoteAt)}</Text>}
                  </HStack>
                </Box>
              ))}

              <Divider />

              {/* Add Note box (attaches to LATEST by groupId + createdAt) */}
              <Box
                borderWidth="1px"
                rounded="md"
                p={4}
                bg="gray.50"
                borderColor="gray.200"
              >
                <Text fontWeight="semibold" mb={2}>
                  Add note (attaches to latest item)
                </Text>
                <Textarea
                  placeholder="Operational note (PHI allowed)."
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  rows={4}
                />
                <HStack justify="space-between" mt={3}>
                  <Text fontSize="xs" color="gray.600">
                    Notes are internal only.
                  </Text>
                  <Button
                    size="sm"
                    colorScheme="blue"
                    onClick={() =>
                      onAddNote(groupId, latest?.createdAt, noteText, () =>
                        setNoteText("")
                      )
                    }
                    isDisabled={!noteText?.trim() || !latest}
                    _hover={hoverScale1}
                  >
                    Save note
                  </Button>
                </HStack>
              </Box>
            </VStack>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

// ==========================
// Main page
// ==========================
const NotificationsPage = () => {
  const toast = useToast();
  const { getIdToken } = useCognito();

  const [loading, setLoading] = useState(true);
  const [resolvingGroup, setResolvingGroup] = useState(null);

  const [actionItems, setActionItems] = useState([]);
  const [confirmItems, setConfirmItems] = useState([]);
  const [updateItems, setUpdateItems] = useState([]);

  const [activeTab, setActiveTab] = useState(0);

  // timeline state
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [timelineGroupId, setTimelineGroupId] = useState(null);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineItems, setTimelineItems] = useState([]);

  // polling handle
  const pollRef = useRef(null);

  const getToken = useCallback(async () => {
    try {
      const token = await getIdToken();
      return token || "";
    } catch (e) {
      console.warn("Could not fetch ID token:", e.message);
      return "";
    }
  }, [getIdToken]);

  const pushCountsToBell = useCallback((action, confirm, update) => {
    try {
      const total = action + confirm + update;
      window.dispatchEvent(
        new CustomEvent(COUNTS_EVENT, {
          detail: { action, confirm, update, total },
        })
      );
    } catch {
      // non-fatal
    }
  }, []);

  const fetchBucket = useCallback(
    async (bucket) => {
      try {
        const token = await getToken();
        if (!token) return [];

        const data = await apiGet(
          `/notifications?bucket=${bucket}&status=open&limit=50`,
          token
        );
        return data.items || [];
      } catch (error) {
        console.warn(
          `Failed to fetch ${bucket} notifications:`,
          error?.message || error
        );
        return [];
      }
    },
    [getToken]
  );

  const refreshAll = useCallback(async () => {
    try {
      setLoading(true);
      const [a, c, u] = await Promise.all([
        fetchBucket("action_required"),
        fetchBucket("confirm"),
        fetchBucket("update"),
      ]);
      setActionItems(a);
      setConfirmItems(c);
      setUpdateItems(u);

      // Push counts to NotificationBell so it can stop polling
      pushCountsToBell(a.length, c.length, u.length);
    } catch (e) {
      console.warn("Error refreshing notifications:", e.message);
      if (!String(e.message).includes("403")) {
        toast({
          title: "Load failed",
          description: e.message,
          status: "error",
        });
      }
    } finally {
      setLoading(false);
    }
  }, [fetchBucket, pushCountsToBell, toast]);

  useEffect(() => {
    refreshAll();
    // start polling
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => {
      refreshAll();
    }, POLL_MS);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      // Let the bell know we're done driving counts
      try {
        window.dispatchEvent(new Event(COUNTS_END_EVENT));
      } catch {
        /* ignore */
      }
    };
  }, [refreshAll]);

  const openTimeline = useCallback(
    async (groupId) => {
      setTimelineGroupId(groupId);
      onOpen();
      setTimelineLoading(true);
      try {
        const token = await getToken();
        if (!token) return;
        const data = await apiGet(
          `/groups/${encodeURIComponent(groupId)}`,
          token
        );
        setTimelineItems(data.items || []);
      } catch (e) {
        console.warn("Timeline error:", e.message);
        if (!String(e.message).includes("403")) {
          toast({
            title: "Timeline error",
            description: e.message,
            status: "error",
          });
        }
      } finally {
        setTimelineLoading(false);
      }
    },
    [getToken, onOpen, toast]
  );

  const addNote = useCallback(
    async (groupId, createdAt, text, after) => {
      try {
        const token = await getToken();
        if (!token) return;
        await apiPost(
          `/notifications/note`,
          {
            groupId,
            createdAt,
            text,
            authorUserId: "admin", // swap for actual userId if you include it in auth
          },
          token
        );
        toast({ title: "Note saved", status: "success" });
        if (after) after();
        // refresh timeline for lastNoteAt
        const data = await apiGet(
          `/groups/${encodeURIComponent(groupId)}`,
          token
        );
        setTimelineItems(data.items || []);
      } catch (e) {
        console.warn("Save failed:", e.message);
        if (!String(e.message).includes("403")) {
          toast({
            title: "Save failed",
            description: e.message,
            status: "error",
          });
        }
      }
    },
    [getToken, toast]
  );

  const resolveLatest = useCallback(
    async (groupId) => {
      try {
        setResolvingGroup(groupId);
        const token = await getToken();
        if (!token) return;
        await apiPost(
          `/groups/resolveLatest`,
          { groupId, resolvedBy: "admin" }, // pipe actual email/user later
          token
        );
        toast({ title: "Resolved", status: "success" });
        // refresh main lists
        await refreshAll();
        // if drawer open on this group, refresh timeline
        if (timelineGroupId === groupId) {
          const data = await apiGet(
            `/groups/${encodeURIComponent(groupId)}`,
            token
          );
          setTimelineItems(data.items || []);
        }
      } catch (e) {
        console.warn("Resolve failed:", e.message);
        if (!String(e.message).includes("403")) {
          toast({
            title: "Resolve failed",
            description: e.message,
            status: "error",
          });
        }
      } finally {
        setResolvingGroup(null);
      }
    },
    [getToken, refreshAll, timelineGroupId, toast]
  );

  const totals = useMemo(
    () => ({
      action: actionItems.length,
      confirm: confirmItems.length,
      update: updateItems.length,
    }),
    [actionItems, confirmItems, updateItems]
  );

  return (
    <Box px={6} py={6}>
      <HStack justify="space-between" align="center" mb={6}>
        <Heading size="lg">Notifications</Heading>
        <HStack>
          <Button
            onClick={refreshAll}
            leftIcon={<TimeIcon />}
            variant="outline"
            _hover={hoverScale1}
          >
            Refresh
          </Button>
        </HStack>
      </HStack>

      <SimpleGrid columns={[1, 3]} spacing={4} mb={4}>
        <Stat
          p={4}
          borderWidth="1px"
          rounded="xl"
          bg="white"
          borderColor="gray.200"
        >
          <StatLabel>Action Required</StatLabel>
          <StatNumber>{totals.action}</StatNumber>
        </Stat>
        <Stat
          p={4}
          borderWidth="1px"
          rounded="xl"
          bg="white"
          borderColor="gray.200"
        >
          <StatLabel>Confirmations</StatLabel>
          <StatNumber>{totals.confirm}</StatNumber>
        </Stat>
        <Stat
          p={4}
          borderWidth="1px"
          rounded="xl"
          bg="white"
          borderColor="gray.200"
        >
          <StatLabel>Updates</StatLabel>
          <StatNumber>{totals.update}</StatNumber>
        </Stat>
      </SimpleGrid>

      <Tabs
        index={activeTab}
        onChange={setActiveTab}
        colorScheme="teal"
        isFitted
      >
        <TabList>
          <Tab>Action Required ({totals.action})</Tab>
          <Tab>Confirmations ({totals.confirm})</Tab>
          <Tab>Updates ({totals.update})</Tab>
        </TabList>
        <TabPanels>
          <TabPanel>
            {loading ? (
              <Spinner />
            ) : actionItems.length === 0 ? (
              <Text color="gray.600">Nothing needs attention.</Text>
            ) : (
              <VStack align="stretch" spacing={4}>
                {actionItems.map((n) => (
                  <NotificationCard
                    key={`${n.groupId}-${n.createdAt}`}
                    n={n}
                    onOpenTimeline={openTimeline}
                    onResolveLatest={resolveLatest}
                    isResolving={resolvingGroup}
                  />
                ))}
              </VStack>
            )}
          </TabPanel>

          <TabPanel>
            {loading ? (
              <Spinner />
            ) : confirmItems.length === 0 ? (
              <Text color="gray.600">No new confirmations.</Text>
            ) : (
              <VStack align="stretch" spacing={4}>
                {confirmItems.map((n) => (
                  <NotificationCard
                    key={`${n.groupId}-${n.createdAt}`}
                    n={n}
                    onOpenTimeline={openTimeline}
                    onResolveLatest={resolveLatest}
                    isResolving={resolvingGroup}
                  />
                ))}
              </VStack>
            )}
          </TabPanel>

          <TabPanel>
            {loading ? (
              <Spinner />
            ) : updateItems.length === 0 ? (
              <Text color="gray.600">No updates right now.</Text>
            ) : (
              <VStack align="stretch" spacing={4}>
                {updateItems.map((n) => (
                  <NotificationCard
                    key={`${n.groupId}-${n.createdAt}`}
                    n={n}
                    onOpenTimeline={openTimeline}
                    onResolveLatest={resolveLatest}
                    isResolving={resolvingGroup}
                  />
                ))}
              </VStack>
            )}
          </TabPanel>
        </TabPanels>
      </Tabs>

      <TimelineDrawer
        isOpen={isOpen}
        onClose={() => {
          setTimelineGroupId(null);
          onClose();
        }}
        groupId={timelineGroupId}
        items={timelineItems}
        loading={timelineLoading}
        onAddNote={addNote}
        onResolveLatest={resolveLatest}
        isResolving={resolvingGroup}
      />
    </Box>
  );
};

export default NotificationsPage;
